import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import Loader from './Loader';

/* A textarea where typing @ suggests people you can tag: your connections, plus
   people already in this conversation. Picking one writes @handle into the text;
   it's shown as their name once posted. */
export default function MentionTextarea({ value, onChange, postId, loadPeople, focusKey, className = 'ta', ...rest }) {
  const ref = useRef(null);
  // A new focusKey (e.g. "Reply" on a comment) puts the cursor at the end of the text.
  useEffect(() => {
    const el = ref.current;
    if (!focusKey || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusKey]);
  // Grows with what's written (up to a cap, then scrolls) — a fixed box hid
  // half of a long comment.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, Math.round(window.innerHeight * 0.5))}px`;
  }, [value]);
  const [people, setPeople] = useState(null);
  const [query, setQuery] = useState(null); // text after @, or null when not tagging
  const [active, setActive] = useState(0);

  useEffect(() => { setPeople(null); }, [postId]);

  function detect(text, caret) {
    const before = text.slice(0, caret);
    const m = before.match(/(?:^|[\s(])@([a-z0-9-]{0,40})$/i);
    setQuery(m ? m[1].toLowerCase() : null);
    setActive(0);
    if (m && people === null) {
      (loadPeople ? loadPeople() : api.mentionCandidates(postId)).then(setPeople).catch(() => setPeople([]));
    }
  }

  const matches = (people || []).filter((p) => !query
    || p.name.toLowerCase().includes(query) || p.handle.includes(query)).slice(0, 6);

  function pick(p) {
    const el = ref.current;
    const caret = el.selectionStart;
    const before = value.slice(0, caret).replace(/@([a-z0-9-]{0,40})$/i, `@${p.handle} `);
    const next = before + value.slice(caret);
    onChange({ target: { value: next } });
    setQuery(null);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(before.length, before.length); });
  }

  function onKeyDown(e) {
    if (query === null || matches.length === 0) return rest.onKeyDown?.(e);
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(matches[active]); }
    else if (e.key === 'Escape') setQuery(null);
    return undefined;
  }

  return (
    <div className="mention-wrap">
      <textarea
        {...rest} ref={ref} className={className} value={value}
        onChange={(e) => { onChange(e); detect(e.target.value, e.target.selectionStart); }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
      />
      {query !== null && (
        <div className="mention-pop" role="listbox">
          {people === null && <Loader inline delay={0} />}
          {people !== null && matches.length === 0 && (
            <div className="muted-sm mention-none">
              {loadPeople ? 'You can tag people in this group.' : <>You can tag your connections and people in this conversation. Not connected yet?
              Ask Copilot to introduce you.</>}
            </div>
          )}
          {matches.map((p, i) => (
            <button key={p.id} type="button" role="option" aria-selected={i === active}
              className={`mention-opt${i === active ? ' on' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}>
              <b>{p.name}</b> <span className="muted-sm">{p.headline}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
